<div class="relative">
  <div
    #bar
    class="bg-surface flex min-w-0 items-center gap-2 rounded-xs border px-2.5 py-1.5 transition-[border-color,box-shadow]"
    [class]="
      error()
        ? 'border-danger focus-within:shadow-[0_0_0_3px_color-mix(in_oklch,var(--danger)_25%,transparent)]'
        : 'border-default focus-within:border-accent-focus focus-within:shadow-[0_0_0_3px_color-mix(in_oklch,var(--accent-focus)_25%,transparent)]'
    ">
    <i
      [class]="ICONS.magnifyingGlass"
      aria-hidden="true"
      class="text-text-tertiary shrink-0 text-sm"></i>
    <div class="relative min-w-0 flex-1">
      <!-- prettier-ignore -->
      <div #overlay aria-hidden="true" class="pointer-events-none absolute inset-0 overflow-hidden font-mono text-[12.5px] leading-5 whitespace-pre [font-variant-ligatures:none]">@for (segment of segments(); track $index) {<span [attr.data-role]="segment.role" [class]="segmentClass(segment)">{{ segment.text }}</span>}</div>
      <input
        #input
        type="text"
        spellcheck="false"
        autocomplete="off"
        class="placeholder:text-text-tertiary relative block w-full border-none bg-transparent p-0 font-mono text-[12.5px] leading-5 text-transparent [caret-color:var(--text-primary)] outline-none [font-variant-ligatures:none] selection:bg-[color-mix(in_oklch,var(--accent-focus)_35%,transparent)] selection:text-transparent"
        [value]="text()"
        [placeholder]="'features.data.path_filter.search.placeholder' | translate"
        [attr.aria-label]="'features.data.path_filter.search.label' | translate"
        [attr.aria-invalid]="error() ? 'true' : null"
        [attr.aria-describedby]="error() ? errorId : null"
        role="combobox"
        aria-autocomplete="list"
        [attr.aria-expanded]="suggestions().length > 0"
        [attr.aria-controls]="suggestions().length > 0 ? listId : null"
        [attr.aria-activedescendant]="activeOptionId()"
        (input)="
          onInput($any($event.target).value, $any($event.target).selectionStart);
          syncScroll(input, overlay)
        "
        (scroll)="syncScroll(input, overlay)"
        (focus)="focused.set(true); reopen(input)"
        (blur)="focused.set(false)"
        (click)="reopen(input)"
        (keyup)="trackCaret(input)"
        (keydown)="onKeydown($event)" />
    </div>
    @if (text()) {
      <button
        type="button"
        class="text-text-tertiary hover:text-text-primary hover:bg-interactive -my-0.5 flex shrink-0 cursor-pointer items-center rounded-xs p-0.5 text-xs transition-colors"
        [attr.aria-label]="'features.data.path_filter.search.clear' | translate"
        (mousedown)="$event.preventDefault()"
        (click)="clear()">
        <i [class]="ICONS.x" aria-hidden="true"></i>
      </button>
    }
  </div>
  @if (error(); as parseError) {
    <p
      role="alert"
      class="bg-elevated border-default text-danger absolute top-full left-0 z-10 mt-1 max-w-full rounded-md border px-2 py-1 text-xs shadow-md"
      [id]="errorId">
      {{
        'features.data.path_filter.search.error'
          | translate: { position: parseError.start + 1 }
      }}
      <span class="font-mono [font-variant-ligatures:none]">{{ parseError.message }}</span>
    </p>
  }
  <app-dropdown
    [target]="bar"
    [template]="suggestionMenu"
    [isOpen]="isOpen()"
    [closeOnClickOutside]="false"
    [onClose]="closeSuggestions"
    [useDefaultStyles]="false" />
</div>

<ng-template #suggestionMenu>
  <div class="g-menu w-[26rem] max-w-[calc(100vw-2rem)]">
    @if (suggestions().length > 0) {
      <div
        class="g-menu-scroll"
        role="listbox"
        [id]="listId"
        [attr.aria-label]="'features.data.path_filter.search.suggestions' | translate">
        @for (
          suggestion of suggestions();
          track suggestion.section + suggestion.label;
          let i = $index
        ) {
          @if (startsSection(i); as section) {
            <div class="g-menu-label" role="presentation">
              {{ 'features.data.path_filter.search.sections.' + section | translate }}
            </div>
          }
          <button
            type="button"
            role="option"
            tabindex="-1"
            class="g-menu-item w-full"
            [id]="optionId(i)"
            [class.is-focused]="i === highlighted()"
            [attr.aria-selected]="i === highlighted()"
            (mousedown)="$event.preventDefault()"
            (click)="apply(suggestion)">
            @if (iconOf(suggestion); as icon) {
              <i [class]="icon" aria-hidden="true"></i>
            }
            <span
              class="ellipsis shrink-0 font-mono text-[12px] [font-variant-ligatures:none]"
              data-suggestion-label
              >{{ suggestion.label }}</span
            >
            @if (suggestion.detail) {
              <span
                class="ellipsis text-text-tertiary ml-auto min-w-0 pl-3 text-xs font-normal"
                >{{ suggestion.detail }}</span
              >
            }
          </button>
        }
      </div>
    }
    @if (hint(); as valueHint) {
      <p class="g-menu-note pt-1.5" role="note">
        {{ 'features.data.path_filter.search.hints.' + valueHint | translate }}
      </p>
    }
    @if (list()?.sortContext && suggestions().length > 0) {
      <p class="g-menu-note pt-1.5">
        {{ 'features.data.path_filter.search.sort_desc' | translate }}
      </p>
    }
    @if (suggestions().length > 0) {
      <div
        class="g-menu-footer text-text-tertiary justify-start gap-3 text-[11px]"
        aria-hidden="true">
        <span class="flex items-center gap-1">
          <kbd
            class="border-default bg-elevated rounded-xs border px-1 font-mono text-[10px]"
            >↑↓</kbd
          >
          {{ 'features.data.path_filter.search.keys.move' | translate }}
        </span>
        <span class="flex items-center gap-1">
          <kbd
            class="border-default bg-elevated rounded-xs border px-1 font-mono text-[10px]"
            >⏎</kbd
          >
          {{ 'features.data.path_filter.search.keys.insert' | translate }}
        </span>
        <span class="flex items-center gap-1">
          <kbd
            class="border-default bg-elevated rounded-xs border px-1 font-mono text-[10px]"
            >esc</kbd
          >
          {{ 'features.data.path_filter.search.keys.close' | translate }}
        </span>
      </div>
    }
  </div>
</ng-template>
